/**
 * Global Fetch Interceptor for Dual-Token Auth
 *
 * Wraps window.fetch to transparently handle 401 responses by refreshing
 * the access token and retrying the request. This avoids modifying every
 * service file individually.
 *
 * Install once at app startup via installFetchInterceptor(tokenManager).
 * When TokenManager is disabled, the interceptor is a pure passthrough.
 */

import { API_BASE } from '../config/api';
import i18n from '../i18n';

let installed = false;
// When a refresh fails we briefly stop attempting more, so a logged-out client
// hitting authenticated endpoints doesn't hammer /refresh-v2 in a loop.
let lastRefreshFailAt = 0;
const REFRESH_FAIL_COOLDOWN_MS = 30 * 1000;

// Only our own backend may receive the Bearer token. Without this check, a
// 401 from any third-party host fetched directly would get retried with our
// access token attached, leaking it off-site.
function isOwnApiUrl(urlStr) {
  if (urlStr.startsWith('/')) return true; // relative — same origin
  try {
    const target = new URL(urlStr, window.location.origin);
    return target.origin === window.location.origin
      || target.origin === new URL(API_BASE, window.location.origin).origin;
  } catch (e) {
    return false;
  }
}

// Every call to our own API names the viewer's active UI language, so the
// server can title places in it (server/utils/viewerLanguage.js). A caller's
// own value wins; a Request object carries its own headers and is left alone.
const LANGUAGE_HEADER = 'X-LYF-Language';

// Except the image byte routes: once photos move to the CDN they answer with a
// cross-origin redirect, and a custom header on that request makes the browser
// ask the CDN's permission first (a CORS preflight). An image has no title to
// localise. (photoShare fetches these to build a shareable file.)
const IMAGE_BYTE_ROUTE = /\/api\/user-(photos|images)\/[A-Za-z0-9_-]{16}(?:[/?#]|$)/;

function withViewerLanguage(url, options) {
  if (typeof url !== 'string' && !(url instanceof URL)) return options;
  if (!isOwnApiUrl(String(url)) || IMAGE_BYTE_ROUTE.test(String(url))) return options;
  const lang = String(i18n.language || 'en').split('-')[0];
  const has = (key) => String(key).toLowerCase() === LANGUAGE_HEADER.toLowerCase();
  const { headers } = options;
  if (typeof Headers !== 'undefined' && headers instanceof Headers) {
    if (headers.has(LANGUAGE_HEADER)) return options;
    const next = new Headers(headers);
    next.set(LANGUAGE_HEADER, lang);
    return { ...options, headers: next };
  }
  if (Array.isArray(headers)) {
    if (headers.some(([key]) => has(key))) return options;
    return { ...options, headers: [...headers, [LANGUAGE_HEADER, lang]] };
  }
  if (headers && Object.keys(headers).some(has)) return options;
  return { ...options, headers: { ...(headers || {}), [LANGUAGE_HEADER]: lang } };
}

/** The window event AccountStateBanner listens for. */
export const ACCOUNT_REFUSAL_EVENT = 'lyf:account-refusal';

let announcerInstalled = false;

/**
 * A write the account's moderation state refuses (policy §4) is announced
 * app-wide, so AccountStateBanner shows the server's own words whichever
 * service made the call and however it handles errors. Installed at startup
 * whatever the token mode (installFetchInterceptor is dual-token only); it
 * only looks at 403s from our own API and changes nothing.
 */
export function installAccountRefusalAnnouncer() {
  if (announcerInstalled) return;
  announcerInstalled = true;
  const innerFetch = window.fetch.bind(window);
  window.fetch = async function accountRefusalFetch(url, options) {
    const response = await innerFetch(url, options);
    const urlStr = typeof url === 'string' ? url : (url?.url || String(url));
    if (response && response.status === 403) {
      if (isOwnApiUrl(urlStr)) announceAccountRefusal(response);
    } else if (response?.ok && isOwnApiUrl(urlStr)) {
      // A create a restricted account asked to share is saved private and the
      // response says so (`visibilityRefusedMessage`, or the recap draft's
      // `visibility_refused_message`): announced the same way. Writes only.
      const method = String(options?.method || (typeof url !== 'string' && url?.method) || 'GET').toUpperCase();
      const type = response.headers?.get?.('content-type') || '';
      if (method !== 'GET' && method !== 'HEAD' && type.includes('application/json')) announceClampedCreate(response);
    }
    return response;
  };
}

function announceClampedCreate(response) {
  try {
    response.clone().json().then((body) => {
      const message = body?.visibilityRefusedMessage || body?.visibility_refused_message;
      if (message) {
        window.dispatchEvent(new CustomEvent(ACCOUNT_REFUSAL_EVENT, { detail: { code: 'account_restricted', message } }));
      }
    }).catch(() => {});
  } catch { /* not readable: nothing to announce */ }
}

/** Read a 403's body off a clone (the caller still reads the original). */
function announceAccountRefusal(response) {
  try {
    response.clone().json().then((body) => {
      if (body?.moderated && /^account_/.test(body.code || '') && body.error) {
        window.dispatchEvent(new CustomEvent(ACCOUNT_REFUSAL_EVENT, { detail: { code: body.code, message: body.error } }));
      }
    }).catch(() => {});
  } catch { /* not readable: nothing to announce */ }
}

export function installFetchInterceptor(tokenManager) {
  if (installed) return;
  installed = true;

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function interceptedFetch(url, options = {}) {
    // Reassigned, so the token-refresh retry below carries the header too.
    options = withViewerLanguage(url, options);
    const response = await originalFetch(url, options);
    const urlStr = typeof url === 'string' ? url : url.toString();

    // Only intercept 401s when dual-token is active
    if (response.status !== 401 || !tokenManager.isEnabled()) {
      return response;
    }

    // Don't intercept the refresh/signin/signout endpoints themselves (avoids recursion).
    // Other /api/auth/* endpoints (me, profile, timezone, etc.) DO need retry.
    if (/\/api\/auth\/(refresh|signin|signup|signout)/.test(urlStr)) {
      return response;
    }

    // Never attach our token to requests bound for other origins
    if (!isOwnApiUrl(urlStr)) {
      return response;
    }

    // Don't retry if the request was aborted
    if (options.signal && options.signal.aborted) {
      return response;
    }

    // NOTE: we intentionally do NOT require the outgoing request to already
    // carry a Bearer header. The access token is in-memory only, so a refresh
    // that failed transiently (e.g. a dropped connection while travelling)
    // nulls the token — and every subsequent request then goes out with no
    // Authorization header. Gating retry on a present Bearer header would
    // strand the user in that state until a manual re-login. A successful
    // refresh below proves a session still exists; if none does, the refresh
    // returns null and we pass the original 401 through unchanged.
    if (Date.now() - lastRefreshFailAt < REFRESH_FAIL_COOLDOWN_MS) {
      return response;
    }

    // Try to refresh the access token
    const newToken = await tokenManager.refreshAccessToken();
    if (!newToken) {
      // Refresh failed — token expired callback already fired by TokenManager
      lastRefreshFailAt = Date.now();
      return response;
    }

    // Retry the original request with the new token
    const headers = options.headers || {};
    const retryOptions = {
      ...options,
      headers: {
        ...headers,
        Authorization: `Bearer ${newToken}`
      }
    };
    return originalFetch(url, retryOptions);
  };
}
